import { Link } from '@tanstack/react-router';
import { useState, type ReactNode } from 'react';
import { ENVIRONMENT_LABELS, type BackupDto } from '@visualitics-toolkit/shared';

import { Badge, Button, EmptyState, EnvDot } from '../components/atoms';
import {
  ErrorBlock,
  LoadingBlock,
  StatusBlock,
  formatBytes,
  formatDateTime,
  formatDuration,
} from '../components/molecules';
import { BackupDialog } from '../components/organisms/BackupDialog';
import { RestoreDialog } from '../components/organisms/RestoreDialog';
import { useActiveConnection, useBackups, useRuns, useTestConnection } from '../lib/queries';

const SHORTCUTS = [
  { to: '/ops/org-extract', label: 'Extrair organização' },
  { to: '/ops/bronze-gold-validate', label: 'Validar Bronze × Gold' },
  { to: '/ops/snapshots-latest', label: 'Últimos snapshots' },
  { to: '/runs', label: 'Ver runs' },
];

export function HomePage(): ReactNode {
  const connection = useActiveConnection();
  const runs = useRuns({ limit: 5 });
  const backups = useBackups(connection?.id);
  const test = useTestConnection();
  const [backupOpen, setBackupOpen] = useState(false);
  const [restoreTarget, setRestoreTarget] = useState<BackupDto | null>(null);

  if (!connection) {
    return (
      <EmptyState
        title="Nenhuma conexão ativa"
        description="Cadastre a conexão com o Postgres do Visualitics para começar."
        action={
          <Link to="/settings" className="text-accent underline">
            Ir para Configurações
          </Link>
        }
      />
    );
  }

  const restoreAllowed = connection.environment !== 'production';

  return (
    <div className="space-y-6">
      <section className="card space-y-3" data-env={connection.environment}>
        <header className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <EnvDot environment={connection.environment} />
            <h1 className="text-lg font-semibold text-content">{connection.name}</h1>
            <Badge tone={connection.environment === 'production' ? 'danger' : 'accent'}>
              {ENVIRONMENT_LABELS[connection.environment]}
            </Badge>
          </div>
          <div className="flex gap-2">
            <Button loading={test.isPending} onClick={() => test.mutate(connection.id)}>
              Testar conexão
            </Button>
            <Button variant="primary" data-testid="btn-backup" onClick={() => setBackupOpen(true)}>
              Backup do banco
            </Button>
          </div>
        </header>

        <dl className="grid gap-3 text-sm sm:grid-cols-2 lg:grid-cols-4">
          <Info label="Host" value={`${connection.host}:${connection.port}`} />
          <Info label="Database" value={connection.database} />
          <Info label="Versão do servidor" value={connection.serverVersion ?? '—'} />
          <Info label="Último backup" value={formatDateTime(connection.lastBackupAt)} />
          <Info label="Usuário" value={connection.username} />
          <Info label="SSL" value={connection.sslmode} />
          <Info label="Último teste" value={formatDateTime(connection.lastTestedAt)} />
          <Info
            label="Resultado do teste"
            value={connection.lastTestOk === null ? '—' : connection.lastTestOk ? 'ok' : 'falhou'}
          />
        </dl>

        {test.isSuccess && test.data && (
          <StatusBlock
            message={`Conexão ok via ${test.data.resolvedHost} — PostgreSQL ${test.data.serverVersion} em ${test.data.latencyMs} ms`}
          />
        )}
        {test.error instanceof Error && <ErrorBlock message={test.error.message} />}

        {connection.sshTunnelNote && (
          <p className="rounded-md border border-border px-3 py-2 text-xs text-muted">
            {connection.sshTunnelNote}
          </p>
        )}
      </section>

      <section className="flex flex-wrap gap-2">
        {SHORTCUTS.map((shortcut) => (
          <Link
            key={shortcut.to}
            to={shortcut.to}
            className="rounded-md border border-border px-3 py-2 text-sm text-content hover:border-accent"
          >
            {shortcut.label}
          </Link>
        ))}
      </section>

      <div className="grid gap-6 lg:grid-cols-2">
        <section className="card">
          <header className="mb-2 flex items-center justify-between">
            <h2 className="text-sm font-medium text-content">Últimas execuções</h2>
            <Link to="/runs" className="text-xs text-accent underline">
              ver todas
            </Link>
          </header>
          {runs.isLoading && <LoadingBlock />}
          <ul className="space-y-1 text-sm">
            {runs.data?.map((run) => (
              <li key={run.id} className="flex items-center justify-between gap-2">
                <Link to="/runs/$runId" params={{ runId: run.id }} className="truncate hover:text-accent">
                  {run.operationId}
                </Link>
                <span className="flex shrink-0 items-center gap-2">
                  <Badge tone={runStatusTone(run.status)}>{run.status}</Badge>
                  <span className="font-mono text-xs text-muted">
                    {formatDuration(run.executeMs)}
                  </span>
                </span>
              </li>
            ))}
            {runs.data?.length === 0 && <li className="text-muted">Nenhuma execução ainda.</li>}
          </ul>
        </section>

        <section className="card">
          <h2 className="mb-2 text-sm font-medium text-content">Últimos backups</h2>
          {backups.isLoading && <LoadingBlock />}
          <ul className="space-y-1 text-sm">
            {backups.data?.slice(0, 5).map((backup) => (
              <li
                key={backup.id}
                className="flex items-center justify-between gap-2"
                title={backup.status === 'failed' ? (backup.errorMessage ?? undefined) : undefined}
              >
                <span className="truncate font-mono text-xs">{backup.filename ?? backup.mode}</span>
                <span className="flex shrink-0 items-center gap-2">
                  <Badge
                    tone={
                      backup.status === 'completed'
                        ? 'success'
                        : backup.status === 'failed'
                          ? 'danger'
                          : 'neutral'
                    }
                  >
                    {backup.status}
                  </Badge>
                  <span className="font-mono text-xs text-muted">{formatBytes(backup.bytes)}</span>
                  {backup.status === 'completed' && (
                    <>
                      <a
                        className="text-xs text-accent underline"
                        href={`/api/v1/backups/${backup.id}/download`}
                      >
                        baixar
                      </a>
                      {restoreAllowed ? (
                        <button
                          type="button"
                          className="text-xs text-accent underline"
                          data-testid="btn-restore"
                          onClick={() => setRestoreTarget(backup)}
                        >
                          restaurar
                        </button>
                      ) : (
                        <span
                          className="text-xs text-muted"
                          title="Restore em produção é runbook manual"
                        >
                          restaurar bloqueado
                        </span>
                      )}
                    </>
                  )}
                </span>
              </li>
            ))}
            {backups.data?.length === 0 && <li className="text-muted">Nenhum backup ainda.</li>}
          </ul>
        </section>
      </div>

      {backupOpen && <BackupDialog connection={connection} onClose={() => setBackupOpen(false)} />}
      {restoreTarget && (
        <RestoreDialog
          backup={restoreTarget}
          connection={connection}
          onClose={() => setRestoreTarget(null)}
        />
      )}
    </div>
  );
}

function Info({ label, value }: { label: string; value: string }): ReactNode {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-muted">{label}</dt>
      <dd className="font-mono text-sm text-content">{value}</dd>
    </div>
  );
}

export function runStatusTone(status: string): 'success' | 'danger' | 'warning' | 'neutral' {
  if (status === 'ok') return 'success';
  if (status === 'failed') return 'danger';
  if (status === 'rejected') return 'warning';
  return 'neutral';
}
